import {
  faChevronLeft,
  faChevronRight,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import AddNewBrand from "../components/brands/AddNewBrand";
import { useDispatch, useSelector } from "react-redux";
import { fetchBrands } from "../redux/features/brands/brandsSlice";
import ViewBrand from "../components/brands/ViewBrand";
import UpdateBrand from "../components/brands/UpdateBrand";
import DeleteBrand from "../components/brands/DeleteBrand";
import { ToastContainer } from "react-toastify";
import { ShimmerBadge, ShimmerTable } from "react-shimmer-effects";

function Brands() {
  const dispatch = useDispatch();
  const { brands, getBrandsLoading } = useSelector((state) => state.brands);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    dispatch(fetchBrands());
  }, [dispatch]);

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const filteredBrands =
    brands?.filter((brand) =>
      brand?.name?.toLowerCase().includes(searchTerm.toLowerCase()),
    ) || [];

  const totalPages = Math.ceil(filteredBrands.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentBrands = filteredBrands.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const refetchBrands = async () => {
    await dispatch(fetchBrands());
  };

  if (getBrandsLoading) {
    return (
      <div className="min-h-screen bg-lightPurple/5 flex flex-col">
        <ToastContainer />
        <Navbar />
        <div className="flex-grow p-4 grid grid-cols-1 max-w-7xl w-full mx-auto">
          <div className="my-6 flex justify-between items-center">
            <h1 className="text-4xl font-bold leading-tight">Brands</h1>
            <div className="flex items-center">
              <AddNewBrand refetchBrands={refetchBrands} />
            </div>
          </div>
          <div className="p-8 bg-gray-50 rounded-xl shadow-lg">
            <div className="flex justify-end mb-8">
              <ShimmerBadge width={250} />
            </div>
            <ShimmerTable row={5} col={4} gap={20} />
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-lightPurple/5 flex flex-col">
      <ToastContainer />
      <Navbar />
      <div className="flex-grow grid grid-cols-1 max-w-7xl mx-auto w-full p-4">
        <div className="flex justify-between items-center mb-6 mt-4">
          <h1 className="text-4xl font-bold">Brands</h1>
          <AddNewBrand refetchBrands={refetchBrands} />
        </div>

        <div className="mb-[4.5rem]">
          <div className="p-8 bg-gray-50 rounded-xl shadow-lg">
            <div className="flex justify-end mb-8">
              <input
                type="text"
                placeholder="Search brands..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-72 border border-gray-300 rounded-lg p-2 pl-4 shadow-sm focus:ring-2 focus:ring-purpleColor focus:outline-none transition"
              />
            </div>

            <div className="overflow-x-auto rounded-lg shadow-md">
              <table className="min-w-full bg-white text-center border border-gray-200">
                <thead className="bg-gradient-to-r from-lightPurple to-purpleColor text-white uppercase text-sm font-semibold">
                  <tr>
                    <th className="p-4">ID</th>
                    <th className="p-4">Logo</th>
                    <th className="p-4">Name</th>
                    <th className="p-4">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {currentBrands.length > 0 ? (
                    currentBrands.map((brand) => (
                      <tr
                        key={brand.id}
                        className="hover:bg-gray-50 transition"
                      >
                        <td className="p-4 text-gray-600">{brand.id}</td>
                        <td className="p-4 flex justify-center">
                          <img
                            src={brand.logo}
                            alt={brand.name}
                            className="h-12 w-20"
                          />
                        </td>
                        <td className="p-4 font-medium">{brand.name}</td>

                        <td className="p-4">
                          <div className="flex justify-center gap-3">
                            <ViewBrand brandId={brand.id} />
                            <UpdateBrand
                              brandId={brand.id}
                              refetchBrands={refetchBrands}
                            />
                            <DeleteBrand
                              brandId={brand.id}
                              refetchBrands={refetchBrands}
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="p-10 text-gray-500">
                        No brands found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            <div className="flex flex-col sm:flex-row justify-between items-center mt-12 gap-4">
              <div className="flex items-center gap-2">
                <span className="text-gray-700 font-medium">
                  Rows per page:
                </span>
                <select
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                  className="border border-gray-300 rounded-md px-2 py-1 bg-white shadow-sm focus:ring-2 focus:ring-purpleColor outline-none"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                </select>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium border transition ${
                    currentPage === 1
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-white hover:bg-purple-50 text-purpleColor border-purpleColor"
                  }`}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                  Prev
                </button>

                <span className="text-gray-700 font-medium">
                  Page {currentPage} of {totalPages || 1}
                </span>

                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages || totalPages === 0}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition ${
                    currentPage === totalPages || totalPages === 0
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-lightPurple text-white hover:bg-purpleColor"
                  }`}
                >
                  Next
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default Brands;
